import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import { useI18n } from "../i18n";
import { LoadError } from "../components/common/ui";
import { useHead, usePub } from "./PublicSite";
import { Markdown } from "./Markdown";
import { Cover, FreeCta, Loading, NotFound, usePublicApi } from "./contentShared";

const Card = ({ a, lang, big = false }) => {
  const { t, fmtDate } = useI18n();
  return (
    <Link to={`/${lang}/blog/${a.slug}`} className={`pub-post-card ${big ? "is-big" : ""}`} data-testid={`blog-card-${a.slug}`}>
      {a.cover_url && <Cover url={a.cover_url} eager={big} sizes={big ? "(min-width: 1024px) 1100px, 100vw" : "(min-width: 768px) 50vw, 100vw"} />}
      <div className="pub-post-meta">
        {a.category && <span className="pub-chip">{t(`blog.cat_${a.category}`)}</span>}
        <span className="pub-muted"><Clock className="inline h-3.5 w-3.5" /> {t("blog.readMin", { count: a.read_min })}</span>
      </div>
      <h2 className="pub-post-title">{a.title}</h2>
      <p className="pub-muted m-0">{a.excerpt}</p>
      {!big && <p className="pub-muted m-0 text-xs">{fmtDate(a.published_at, false)}</p>}
    </Link>
  );
};

export const BlogIndex = () => {
  const { lang } = usePub();
  const { t } = useI18n();
  const [sp, setSp] = useSearchParams();
  const cat = sp.get("category") || "";
  const { data } = usePublicApi("/public/blog", lang);
  useHead(t("blog.metaTitle"), t("blog.metaDesc"));
  if (!data) return <Loading />;
  const items = data.items.filter((a) => !cat || a.category === cat);
  const [first, ...rest] = items;
  return (
    <div className="pub-section" data-testid="blog-page">
      <div className="pub-wrap">
        <p className="pub-kicker is-light">{t("blog.kicker")}</p>
        <h1 className="pub-h1">{t("blog.title")}</h1>
        <p className="pub-lead">{t("blog.lead")}</p>
        <div className="pub-chips mt-6" data-testid="blog-categories">
          <button className={`pub-chip ${!cat ? "is-on" : ""}`} onClick={() => setSp({})} data-testid="blog-category-all">{t("blog.all")}</button>
          {data.categories.map((c) => (
            <button key={c} className={`pub-chip ${cat === c ? "is-on" : ""}`} onClick={() => setSp({ category: c })} data-testid={`blog-category-${c}`}>{t(`blog.cat_${c}`)}</button>
          ))}
        </div>
        {items.length === 0 && <p className="pub-muted mt-8" data-testid="blog-empty">{t("blog.empty")}</p>}
        {first && <div className="mt-8"><Card a={first} lang={lang} big /></div>}
        <div className="pub-post-grid mt-8" data-testid="blog-list">{rest.map((a) => <Card key={a.slug} a={a} lang={lang} />)}</div>
        <FreeCta id="blog-index" />
      </div>
    </div>
  );
};

export const BlogArticle = () => {
  const { slug } = useParams();
  const { lang } = usePub();
  const { t, fmtDate } = useI18n();
  const { data, error } = usePublicApi(`/public/blog/${slug}`, lang);
  useHead(data ? `${data.title} | Auditmail` : t("blog.metaTitle"), data ? data.meta_desc : t("blog.metaDesc"),
          { langs: data ? data.langs : undefined, image: data && data.cover_url, type: "article" });
  if (error) return <LoadError error={error} onRetry={() => window.location.reload()} notFound={<NotFound back="/blog" label={t("blog.back")} />} />;
  if (!data) return <Loading />;
  return (
    <article className="pub-section" data-testid="blog-article">
      <div className="pub-wrap pub-article">
        <Link to={`/${lang}/blog`} className="pub-link inline-flex items-center gap-1" data-testid="blog-back-link"><ArrowLeft className="rtl-flip h-4 w-4" />{t("blog.back")}</Link>
        <div className="pub-post-meta mt-6">
          {data.category && <span className="pub-chip">{t(`blog.cat_${data.category}`)}</span>}
          <span className="pub-muted">{fmtDate(data.published_at, false)} · {t("blog.readMin", { count: data.read_min })}</span>
        </div>
        <h1 className="pub-h1 pub-article-h1" data-testid="blog-article-title">{data.title}</h1>
        <p className="pub-lead">{data.excerpt}</p>
        {data.cover_url && <Cover className="pub-article-cover" url={data.cover_url} eager testId="blog-article-cover" />}
        <Markdown source={data.body} testId="blog-article-body" />
        <FreeCta id="article" />
        {data.related.length > 0 && (
          <section className="mt-12" data-testid="blog-related">
            <h2 className="pub-h2">{t("blog.related")}</h2>
            <div className="pub-post-grid mt-4">{data.related.map((a) => <Card key={a.slug} a={a} lang={lang} />)}</div>
          </section>
        )}
      </div>
    </article>
  );
};
